/**
 * Редактори «Зміна податкового призначення» (2026-08-11, рішення власника
 * «а где этот документ?» — backend і списки існували, але БЕЗ маршрутів,
 * меню і редакторів з підпорції 3.1b).
 *
 * 1. TPInventoryEditor — запаси: рядки (номенклатура/к-сть/ціна/старе→нове
 *    призначення), ПДВ-компенсація рахується проведенням.
 * 2. TPTzrEditor — ТЗР: привʼязка до надходження, сума ТЗР, старе→нове.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { tpInventory, tpTzr, type TPInventoryItem } from '@/api/vatAdjustments';
import { goodsReceipts } from '@/api/goodsReceipts';
import { orgs } from '@/api/catalogs';
import { refs } from '@/api/refs';
import DatePopover from '@/components/DatePopover';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import AttachmentsPanel from '@/shared/components/AttachmentsPanel';
import DocLayout from '@/shared/components/DocLayout';
import DocBlock, { type DocBlockState } from '@/shared/components/DocBlock';
import DocRail from '@/shared/components/DocRail';
import NomenclatureSelect from '@/components/NomenclatureSelect';
import NomenclaturePickerModal from '@/components/NomenclaturePickerModal';
import { useConfirm } from '@/components/ConfirmProvider';
import { pickDefaultOrg } from '@/store/defaults';
import TchTable from '@/shared/components/TchTable';

const fmt2 = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function nowLocal(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: unknown } } }).response;
    const d = r?.data?.detail;
    if (typeof d === 'string') return d;
    if (Array.isArray(d)) return d.map((x) => (x as { msg?: string }).msg ?? '').join('; ');
  }
  return String(e);
}

function OrgSelect({ value, onChange, disabled }: {
  value: string; onChange: (v: string) => void; disabled: boolean;
}) {
  const orgsQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  useEffect(() => {
    if (!value && orgsQ.data?.items.length) {
      const def = pickDefaultOrg(orgsQ.data.items);
      if (def) onChange(def.id);
    }
  }, [value, orgsQ.data, onChange]);
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled}>
      <option value="">—</option>
      {(orgsQ.data?.items ?? []).map((o) => (
        <option key={o.id} value={o.id}>{o.name}</option>
      ))}
    </select>
  );
}

function TaxPurposeSelect({ value, onChange, disabled, placeholder }: {
  value: string; onChange: (v: string) => void; disabled: boolean; placeholder?: string;
}) {
  // FK рядків → ref.tax_purposes_assets_costs (НУ-призначення активів),
  // НЕ ref.tax_purposes (то — призначення постачань для проп. ПДВ).
  const q = useQuery({
    queryKey: ['tax-purposes-assets-costs'],
    queryFn: refs.taxPurposesAssetsCosts,
  });
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled}>
      <option value="">{placeholder ?? '—'}</option>
      {(q.data ?? []).map((tp) => (
        <option key={tp.id} value={tp.id}>{tp.name_uk}</option>
      ))}
    </select>
  );
}

// ========================= Запаси =========================

export function TPInventoryEditor() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    organization_id: '', document_number: '', document_date: nowLocal(),
    adjustment_reason: '', comment: '',
    items: [] as TPInventoryItem[],
  });
  // 2026-08-11 (рішення власника): попап номенклатури з мультивибором
  // (як у складському переміщенні).
  const [pickerOpen, setPickerOpen] = useState(false);
  // Призначення для префілу: картка номенклатури його НЕ несе (1С веде
  // по рядках) → останній проведений рядок приходу; fallback TAXABLE.
  const assetsCostsQ = useQuery({
    queryKey: ['tax-purposes-assets-costs'],
    queryFn: refs.taxPurposesAssetsCosts,
  });
  const acByCode = new Map((assetsCostsQ.data ?? []).map((t) => [t.code, t.id]));

  const docQ = useQuery({
    queryKey: ['tp-inventory-doc', id],
    queryFn: () => tpInventory.get(id!),
    enabled: !isNew,
  });
  useEffect(() => {
    const d = docQ.data;
    if (!d) return;
    setForm({
      organization_id: d.organization_id,
      document_number: d.document_number,
      document_date: d.document_date.slice(0, 16),
      adjustment_reason: d.adjustment_reason,
      comment: d.comment ?? '',
      items: d.items.map((it) => ({
        line_number: it.line_number,
        nomenclature_id: it.nomenclature_id,
        characteristic_id: it.characteristic_id ?? null,
        series_id: it.series_id ?? null,
        quantity: String(it.quantity),
        original_unit_price: String(it.original_unit_price),
        old_tax_purpose_id: it.old_tax_purpose_id,
        new_tax_purpose_id: it.new_tax_purpose_id,
        vat_rate: it.vat_rate != null ? String(it.vat_rate) : undefined,
        line_comment: it.line_comment ?? null,
        vat_amount_to_compensate: it.vat_amount_to_compensate,
      })),
    });
  }, [docQ.data]);
  const isPosted = !isNew && (docQ.data?.posted ?? false);

  const payload = () => ({
    organization_id: form.organization_id,
    document_number: form.document_number || null,
    document_date: form.document_date,
    adjustment_reason: form.adjustment_reason,
    comment: form.comment || null,
    items: form.items.map((it, i) => ({
      line_number: i + 1,
      nomenclature_id: it.nomenclature_id,
      characteristic_id: it.characteristic_id ?? null,
      series_id: it.series_id ?? null,
      quantity: it.quantity,
      original_unit_price: it.original_unit_price,
      old_tax_purpose_id: it.old_tax_purpose_id,
      new_tax_purpose_id: it.new_tax_purpose_id,
      // ч.1339: ставка і коментар рядка не губляться при повторному збереженні.
      ...(it.vat_rate ? { vat_rate: it.vat_rate } : {}),
      line_comment: it.line_comment ?? null,
    })),
  });
  const updPayload = () => {
    const p = payload() as Record<string, unknown>;
    delete p.organization_id;
    delete p.document_number;
    return p;
  };
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['tp-inventory'] });
    qc.invalidateQueries({ queryKey: ['tp-inventory-doc', id] });
  };
  const create = useMutation({
    mutationFn: () => tpInventory.create(payload()),
    onSuccess: (d) => { invalidate(); navigate(`/taxation/tax-purpose-adj-inventory/${d.id}`); },
    onError: (e) => setError(extractError(e)),
  });
  const update = useMutation({
    mutationFn: () => tpInventory.update(id!, updPayload()),
    onSuccess: invalidate,
    onError: (e) => setError(extractError(e)),
  });
  const post = useMutation({
    mutationFn: async () => { await tpInventory.update(id!, updPayload()); return tpInventory.post(id!); },
    onSuccess: invalidate,
    onError: (e) => setError(extractError(e)),
  });
  const unpost = useMutation({
    mutationFn: () => tpInventory.unpost(id!),
    onSuccess: invalidate,
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: () => tpInventory.remove(id!),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['tp-inventory'] }); navigate('/taxation/tax-purpose-adj-inventory'); },
    onError: (e) => setError(extractError(e)),
  });

  function setItem(i: number, patch: Partial<TPInventoryItem>) {
    setForm((f) => ({
      ...f,
      items: f.items.map((e, idx) => idx === i ? { ...e, ...patch } : e),
    }));
  }

  // ч.889 (новий дизайн, Закон #10): «ще дій» не було.
  const railActions: { label: string; title?: string; disabled?: boolean; onClick: () => void }[] = [];
  const blocks: DocBlockState[] = [
    { id: 'main', title: 'Основні дані', required: 3,
      filled: (form.organization_id ? 1 : 0) + (form.document_date ? 1 : 0) + (form.adjustment_reason ? 1 : 0) },
    { id: 'items', title: 'Рядки', required: 1, filled: form.items.length > 0 ? 1 : 0 },
    { id: 'extra', title: 'Коментарі та вкладення', required: 0, filled: 0 },
  ];

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          Зміна податкового призначення (запаси) {!isNew && `№ ${form.document_number}`}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center',
          flexWrap: 'wrap', marginLeft: 'auto' }}>
          <DocumentToolbar
            railMode
            extraActions={railActions}
            onBackToList={() => navigate('/taxation/tax-purpose-adj-inventory')}
            onSave={() => (isNew ? create.mutate() : update.mutate())}
            savePending={create.isPending || update.isPending}
            isPosted={isPosted}
            isNew={isNew}
            onPost={() => post.mutate()}
            postPending={post.isPending}
            postDisabled={isNew || form.items.length === 0}
            postTitle={isNew ? 'Спершу збережіть' : 'Зберігає та проводить'}
            onUnpost={() => unpost.mutate()}
            unpostPending={unpost.isPending}
            onDelete={async () => {
              if (await confirm('Видалити документ?')) remove.mutate();
            }}
            entriesDoc={{ type: 'doc.tax_purpose_adjustments_inventory', id: isNew ? '' : id! }}
          />
        </div>
      </div>

      <DocLayout rail={
        <DocRail blocks={blocks} actions={railActions} isNew={isNew}
          doc={{ type: 'doc.tax_purpose_adjustments_inventory', id: isNew ? '' : id! }} />
      }>
      <DocBlock index={1} title="Основні дані">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Організація *</label>
          <OrgSelect value={form.organization_id}
            onChange={(v) => setForm((f) => ({ ...f, organization_id: v }))}
            disabled={!isNew} />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>№ документа</label>
          <input value={form.document_number}
            onChange={(e) => setForm({ ...form, document_number: e.target.value })}
            placeholder="авто" disabled={isPosted} />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Дата *</label>
          <DatePopover withTime value={form.document_date}
            onChange={(v) => setForm({ ...form, document_date: v })}
            disabled={isPosted} />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Причина коригування *</label>
          <input value={form.adjustment_reason}
            onChange={(e) => setForm({ ...form, adjustment_reason: e.target.value })}
            placeholder="напр. «товар переведено у звільнену діяльність»"
            disabled={isPosted} />
        </div>
      </div>

      </DocBlock>

      <DocBlock index={2} title="Рядки"
        actions={!isPosted ? (
          <button type="button" className="primary" onClick={() => setPickerOpen(true)}
            title="Підбір з довідника з залишками; галочками можна обрати кілька товарів одразу">
            + додати номенклатуру
          </button>
        ) : undefined}>
      <TchTable>
        <thead>
          <tr>
            <th style={{ width: 30 }}>#</th>
            <th>Номенклатура</th>
            <th style={{ width: 100 }}>К-сть</th>
            <th style={{ width: 110 }}>Ціна придбання</th>
            <th style={{ width: 180 }}>Старе призначення</th>
            <th style={{ width: 180 }}>Нове призначення</th>
            {isPosted && <th style={{ width: 120 }}>ПДВ компенсація</th>}
            <th style={{ width: 40 }}></th>
          </tr>
        </thead>
        <tbody>
          {form.items.map((it, i) => (
            <tr key={i}>
              <td>{i + 1}</td>
              <td>
                <NomenclatureSelect value={it.nomenclature_id} disabled={isPosted}
                  onChange={(v) => setItem(i, { nomenclature_id: v })} />
              </td>
              <td>
                <input type="number" step="0.001" value={it.quantity}
                  onChange={(e) => setItem(i, { quantity: e.target.value })}
                  disabled={isPosted} />
              </td>
              <td>
                <input type="number" step="0.01" value={it.original_unit_price}
                  onChange={(e) => setItem(i, { original_unit_price: e.target.value })}
                  disabled={isPosted} style={{ textAlign: 'right' }} />
              </td>
              <td>
                <TaxPurposeSelect value={it.old_tax_purpose_id} disabled={isPosted}
                  onChange={(v) => setItem(i, { old_tax_purpose_id: v })} />
              </td>
              <td>
                <TaxPurposeSelect value={it.new_tax_purpose_id} disabled={isPosted}
                  onChange={(v) => setItem(i, { new_tax_purpose_id: v })} />
              </td>
              {isPosted && (
                <td style={{ textAlign: 'right' }}>
                  {it.vat_amount_to_compensate ? fmt2(it.vat_amount_to_compensate) : ''}
                </td>
              )}
              <td>
                {!isPosted && (
                  <button className="danger" style={{ padding: '0.3rem 0.6rem' }}
                    onClick={() => setForm((f) => ({
                      ...f, items: f.items.filter((_, idx) => idx !== i),
                    }))}>×</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </TchTable>
      {pickerOpen && form.organization_id && (
        <NomenclaturePickerModal
          organizationId={form.organization_id}
          documentDate={form.document_date}
          multiSelect
          onPickMany={(rows) => {
            void (async () => {
              // Старе призначення: останній рядок приходу цієї номенклатури
              // (коди спільні між довідниками); нема → TAXABLE.
              let lastByNom: Record<string, string> = {};
              try {
                lastByNom = await refs.lastTaxPurpose(
                  form.organization_id, rows.map((r) => r.nomenclature_id));
              } catch { /* фолбек нижче */ }
              const taxableId = acByCode.get('TAXABLE') ?? '';
              setForm((f) => ({
                ...f,
                items: [...f.items, ...rows.map((r, k) => ({
                  line_number: f.items.length + k + 1,
                  nomenclature_id: r.nomenclature_id,
                  quantity: '1',
                  // Собівартість рядка залишків → ціна придбання (якщо є).
                  original_unit_price: r.cost ?? '0',
                  old_tax_purpose_id:
                    acByCode.get(lastByNom[r.nomenclature_id] ?? '') ?? taxableId,
                  new_tax_purpose_id: '',
                }))],
              }));
              setPickerOpen(false);
            })();
          }}
          onPick={() => undefined}
          onClose={() => setPickerOpen(false)}
        />
      )}

      {isPosted && docQ.data && (
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', margin: '0.5rem 0 0' }}>
          ПДВ до компенсації разом: <b>{fmt2(docQ.data.total_vat_to_compensate)}</b> грн
        </p>
      )}
      </DocBlock>

      <DocBlock index={3} title="Коментарі та вкладення">
      <div className="docblock__cols">
        <div className="field" style={{ margin: 0 }}>
          <label>Коментар</label>
          <input value={form.comment}
            onChange={(e) => setForm({ ...form, comment: e.target.value })}
            disabled={isPosted} />
        </div>
        <div>
          <AttachmentsPanel organizationId={form.organization_id}
            documentType="doc.tax_purpose_adjustments_inventory" documentId={id} />
        </div>
      </div>
      </DocBlock>
      </DocLayout>
      {error && <div className="error">{error}</div>}
    </div>
  );
}

// ========================= ТЗР =========================

export function TPTzrEditor() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    organization_id: '', document_number: '', document_date: nowLocal(),
    adjustment_reason: '', comment: '',
    related_goods_receipt_id: '', tzr_amount: '',
    old_tax_purpose_id: '', new_tax_purpose_id: '', vat_rate: '20.00',
  });

  const docQ = useQuery({
    queryKey: ['tp-tzr-doc', id],
    queryFn: () => tpTzr.get(id!),
    enabled: !isNew,
  });
  useEffect(() => {
    const d = docQ.data;
    if (!d) return;
    setForm({
      organization_id: d.organization_id,
      document_number: d.document_number,
      document_date: d.document_date.slice(0, 16),
      adjustment_reason: d.adjustment_reason,
      comment: d.comment ?? '',
      related_goods_receipt_id: d.related_goods_receipt_id,
      tzr_amount: String(d.tzr_amount),
      old_tax_purpose_id: d.old_tax_purpose_id,
      new_tax_purpose_id: d.new_tax_purpose_id,
      vat_rate: String(d.vat_rate),
    });
  }, [docQ.data]);
  const isPosted = !isNew && (docQ.data?.posted ?? false);

  const receiptsQ = useQuery({
    queryKey: ['receipts-for-tp-tzr', form.organization_id],
    queryFn: () => goodsReceipts.list({
      organization_id: form.organization_id, posted: true, limit: 200,
    }),
    enabled: !!form.organization_id,
  });

  const payload = () => ({
    organization_id: form.organization_id,
    document_number: form.document_number || null,
    document_date: form.document_date,
    adjustment_reason: form.adjustment_reason,
    comment: form.comment || null,
    related_goods_receipt_id: form.related_goods_receipt_id,
    tzr_amount: form.tzr_amount,
    old_tax_purpose_id: form.old_tax_purpose_id,
    new_tax_purpose_id: form.new_tax_purpose_id,
    vat_rate: form.vat_rate,
  });
  const updPayload = () => {
    const p = payload() as Record<string, unknown>;
    delete p.organization_id;
    delete p.document_number;
    return p;
  };
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['tp-tzr'] });
    qc.invalidateQueries({ queryKey: ['tp-tzr-doc', id] });
  };
  const create = useMutation({
    mutationFn: () => tpTzr.create(payload()),
    onSuccess: (d) => { invalidate(); navigate(`/taxation/tax-purpose-adj-tzr/${d.id}`); },
    onError: (e) => setError(extractError(e)),
  });
  const update = useMutation({
    mutationFn: () => tpTzr.update(id!, updPayload()),
    onSuccess: invalidate,
    onError: (e) => setError(extractError(e)),
  });
  const post = useMutation({
    mutationFn: async () => { await tpTzr.update(id!, updPayload()); return tpTzr.post(id!); },
    onSuccess: invalidate,
    onError: (e) => setError(extractError(e)),
  });
  const unpost = useMutation({
    mutationFn: () => tpTzr.unpost(id!),
    onSuccess: invalidate,
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: () => tpTzr.remove(id!),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['tp-tzr'] }); navigate('/taxation/tax-purpose-adj-tzr'); },
    onError: (e) => setError(extractError(e)),
  });

  const compensation = Number(form.tzr_amount || 0) * Number(form.vat_rate || 0) / 100;

  // ч.889 (новий дизайн, Закон #10): «ще дій» не було.
  const railActions: { label: string; title?: string; disabled?: boolean; onClick: () => void }[] = [];
  const blocks: DocBlockState[] = [
    { id: 'main', title: 'Основні дані', required: 2,
      filled: (form.organization_id ? 1 : 0) + (form.document_date ? 1 : 0) },
    { id: 'adj', title: 'Надходження і призначення', required: 5,
      filled: (form.related_goods_receipt_id ? 1 : 0) + (Number(form.tzr_amount) > 0 ? 1 : 0)
        + (form.old_tax_purpose_id ? 1 : 0) + (form.new_tax_purpose_id ? 1 : 0) + (form.adjustment_reason ? 1 : 0) },
    { id: 'extra', title: 'Коментарі та вкладення', required: 0, filled: 0 },
  ];

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          Зміна податкового призначення (ТЗР) {!isNew && `№ ${form.document_number}`}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center',
          flexWrap: 'wrap', marginLeft: 'auto' }}>
          <DocumentToolbar
            railMode
            extraActions={railActions}
            onBackToList={() => navigate('/taxation/tax-purpose-adj-tzr')}
            onSave={() => (isNew ? create.mutate() : update.mutate())}
            savePending={create.isPending || update.isPending}
            isPosted={isPosted}
            isNew={isNew}
            onPost={() => post.mutate()}
            postPending={post.isPending}
            postDisabled={isNew}
            postTitle={isNew ? 'Спершу збережіть' : 'Зберігає та проводить'}
            onUnpost={() => unpost.mutate()}
            unpostPending={unpost.isPending}
            onDelete={async () => {
              if (await confirm('Видалити документ?')) remove.mutate();
            }}
            entriesDoc={{ type: 'doc.tax_purpose_adjustments_tzr', id: isNew ? '' : id! }}
          />
        </div>
      </div>

      <DocLayout rail={
        <DocRail blocks={blocks} actions={railActions} isNew={isNew}
          doc={{ type: 'doc.tax_purpose_adjustments_tzr', id: isNew ? '' : id! }} />
      }>
      <DocBlock index={1} title="Основні дані">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Організація *</label>
          <OrgSelect value={form.organization_id}
            onChange={(v) => setForm((f) => ({ ...f, organization_id: v }))}
            disabled={!isNew} />
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>№ документа</label>
          <input value={form.document_number}
            onChange={(e) => setForm({ ...form, document_number: e.target.value })}
            placeholder="авто" disabled={isPosted} />
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Дата *</label>
          <DatePopover withTime value={form.document_date}
            onChange={(v) => setForm({ ...form, document_date: v })}
            disabled={isPosted} />
        </div>
      </div>
      </DocBlock>

      <DocBlock index={2} title="Надходження і призначення">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Надходження (ТЗР якого коригуємо) *</label>
          <select value={form.related_goods_receipt_id}
            onChange={(e) => setForm({ ...form, related_goods_receipt_id: e.target.value })}
            disabled={isPosted}>
            <option value="">—</option>
            {(receiptsQ.data?.items ?? []).map((r) => (
              <option key={r.id} value={r.id}>
                №{r.document_number} від {new Date(r.document_date).toLocaleDateString('uk-UA')}
              </option>
            ))}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Сума ТЗР (без ПДВ) *</label>
          <input type="number" step="0.01" value={form.tzr_amount}
            onChange={(e) => setForm({ ...form, tzr_amount: e.target.value })}
            disabled={isPosted} style={{ textAlign: 'right' }} />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Старе призначення *</label>
          <TaxPurposeSelect value={form.old_tax_purpose_id} disabled={isPosted}
            onChange={(v) => setForm({ ...form, old_tax_purpose_id: v })} />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Нове призначення *</label>
          <TaxPurposeSelect value={form.new_tax_purpose_id} disabled={isPosted}
            onChange={(v) => setForm({ ...form, new_tax_purpose_id: v })} />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Ставка ПДВ, %</label>
          <input type="number" step="0.01" value={form.vat_rate}
            onChange={(e) => setForm({ ...form, vat_rate: e.target.value })}
            disabled={isPosted} style={{ textAlign: 'right' }} />
        </div>
        <div className="field" style={{ gridColumn: 'span 12' }}>
          <label>Причина коригування *</label>
          <input value={form.adjustment_reason}
            onChange={(e) => setForm({ ...form, adjustment_reason: e.target.value })}
            disabled={isPosted} />
        </div>
      </div>

      {form.tzr_amount && (
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', margin: '0.5rem 0 0' }}>
          ПДВ до компенсації: <b>{fmt2(compensation)}</b> грн
          ({form.tzr_amount} × {form.vat_rate}%)
        </p>
      )}
      </DocBlock>

      <DocBlock index={3} title="Коментарі та вкладення">
      <div className="docblock__cols">
        <div className="field" style={{ margin: 0 }}>
          <label>Коментар</label>
          <input value={form.comment}
            onChange={(e) => setForm({ ...form, comment: e.target.value })}
            disabled={isPosted} />
        </div>
        <div>
          <AttachmentsPanel organizationId={form.organization_id}
            documentType="doc.tax_purpose_adjustments_tzr" documentId={id} />
        </div>
      </div>
      </DocBlock>
      </DocLayout>
      {error && <div className="error">{error}</div>}
    </div>
  );
}
